import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { secureApi } from '../../utils/secureApi';

const commandHelp = {
  swla: { description: 'Switch all sensors off.', example: 'SWLA' },
  swha: { description: 'Switch all sensors on.', example: 'SWHA' },
  smpint: { description: 'Set sample interval and duration (minutes). Interval must be > duration.', example: 'SMPINT,50,20' },
  const: { description: 'Constant sampling mode.', example: 'CONST' },
  inter: { description: 'Interval sampling mode.', example: 'INTER' },
  dev: { description: 'Developer mode.', example: 'DEV' },
  srttm: { description: 'Set start time (epoch seconds).', example: 'SRTTM,1720000000' },
  test: { description: 'Test mode for given duration (seconds).', example: 'TEST,120' },
  ocom: { description: 'Open COM port (2–7).', example: 'OCOM,4' },
  deploy: { description: 'Deploy or end deployment.', example: 'DEPLOY,True / DEPLOY,False' },
  exit: { description: 'Exit the terminal session.', example: 'EXIT' },
  menu: { description: 'Return to the dashboard main menu.', example: 'MENU' },
  ping: {description: 'Check for Connectivity'}
};

const validBaseCommands = new Set(Object.keys(commandHelp));

function isNoiseMessage(msg) {
  let txt = "";
  if (typeof msg === "string") txt = msg;
  else if (msg && typeof msg.message === "string") txt = msg.message;
  else return false;

  return (
    txt.startsWith("CO2-Pro Warmup") ||
    // Only filter out CT data messages, not response messages
    (txt.startsWith("CT") && txt.includes(",") && txt.includes("W M,")) ||
    /^\d{4}-\d{2}-\d{2}/.test(txt) ||
    /^W M,20\d{2}/.test(txt) ||
    /^U[2-9] /.test(txt) ||
    txt.includes("Sensor on...active sampling detected") ||
    txt.includes("TPSN")
  );
}

const TerminalPage = () => {
  const { accessTier, ACCESS_TIERS } = useAuth();
  const [log, setLog] = useState([]);
  const [input, setInput] = useState('');
  const [selectedBuoy, setSelectedBuoy] = useState('CT2001');
  const buoyOptions = ['CT2001'];
  
  const isAdvancedAccess = accessTier === ACCESS_TIERS.ADVANCED;

  const appendLog = (...lines) => {
    setLog(prev => [...prev, ...lines]);
  };

  useEffect(() => {
    console.log('🔌 Attempting SSE connection to: /api/events');
    const eventSource = new EventSource('/api/events');
    eventSource.onmessage = (e) => {
      console.log('📥 Received SSE event:', e.data);
      let msg;
      try {
        msg = JSON.parse(e.data);
      } catch {
        msg = e.data;
      }
      
      console.log('📥 Parsed message:', msg);
      console.log('📥 Is noise message?', isNoiseMessage(msg));
    
      if (!isNoiseMessage(msg)) {
        let text = '';
    
        if (typeof msg === 'string') {
          text = msg;
        } else if (msg?.response) {
          text = msg.response;
        } else if (msg?.message) {
          text = msg.message;
        } else {
          text = JSON.stringify(msg);
        }
        
        console.log('📥 Displaying text:', text);
        appendLog(`💡 Buoy response: ${text}`);
    
        if (text.includes('is ONLINE') || text.includes('did NOT respond')) {
          setTimeout(() => {
            setLog(prev => prev.slice(0, -1));
          }, 5000);
        }
      } else {
        console.log('📥 Message filtered out as noise');
      }
    };
    
    eventSource.onopen = () => {
      console.log('🔗 SSE connection opened');
    };
    
    eventSource.onerror = (error) => {
      console.log('❌ SSE connection error:', error);
      appendLog('⚠️ Lost connection to buoy/response stream.');
      eventSource.close();
    };
    return () => eventSource.close();
  }, []);

  const handleCommand = async (raw) => {
    const trimmed = raw.trim();
    if (!trimmed) return;

    appendLog(`> ${trimmed}`);

    // HELP
    if (trimmed.toLowerCase() === 'help') {
      const output = Object.entries(commandHelp).map(
        ([cmd, { description, example }]) => `${cmd.toUpperCase()} - ${description} (e.g. ${example})`
      );
      appendLog(...output);
      return;
    }

        // View Queue (uses selectedBuoy)
    if (trimmed.toLowerCase() === 'q') {
      try {
        const res = await secureApi.get(`/api/queue/${selectedBuoy}`);
        const data = await res.json();
        if (!data.commands?.length) appendLog(`📭 Queue for ${selectedBuoy} is empty.`);
        else {
          appendLog(`📥 Queue for ${selectedBuoy}:`);
          data.commands.forEach((cmd, i) => appendLog(`  ${i + 1}. ${cmd}`));
        }
      } catch (err) {
        appendLog(`❌ Failed to fetch queue: ${err.message}`);
      }
      return;
    }

    // Delete Queue (uses selectedBuoy)
    if (trimmed.toLowerCase() === 'delete q') {
      try {
        const res = await secureApi.delete(`/api/queue/${selectedBuoy}`);
        if (!res.ok) throw new Error(`Status ${res.status}`);
        appendLog(`🗑️ Cleared queue for ${selectedBuoy}`);
      } catch (err) {
        appendLog(`❌ Failed to clear queue: ${err.message}`);
      }
      return;
    }


    const [cmd, ...args] = trimmed.split(',');

    // Help per command (e.g., SMPINT-h)
    if (cmd.toLowerCase().endsWith('-h')) {
      const base = cmd.slice(0, -2).toLowerCase();
      const help = commandHelp[base];
      help
        ? appendLog(`${base.toUpperCase()}: ${help.description}`, `Example: ${help.example}`)
        : appendLog(`Unknown command: ${base.toUpperCase()}`);
      return;
    }

    const baseCmd = cmd.trim().toLowerCase();
    
    // For advanced access, allow any command. For standard access, restrict to validated commands.
    if (!isAdvancedAccess && !validBaseCommands.has(baseCmd) && baseCmd !== 'ping') {
      appendLog(`❌ Invalid command: "${cmd}". Type "help" for valid options.`);
      appendLog(`💡 Advanced access required for unrestricted commands.`);
      return;
    }

    // Only validate known commands for standard access. Advanced access allows anything.
    if (!isAdvancedAccess) {
      if (baseCmd === 'smpint') {
        if (args.length !== 2) return appendLog('❌ SMPINT requires: interval, duration (in seconds)');
        const [i, d] = args.map(Number);
        if (isNaN(i) || isNaN(d) || i <= 0 || d <= 0 || i <= d)
          return appendLog('❌ SMPINT: interval > duration, both > 0');
      }

      if (baseCmd === 'deploy') {
        if ((args.length !== 1 && args.length !== 4) || !['True', 'False'].includes(args[0]))
          return appendLog('❌ DEPLOY must be DEPLOY,True,... or DEPLOY,False');
      }
    }

    const commandToSend = [cmd.toUpperCase(), ...args].join(',');

    try {
      console.log(`🔄 Sending command: ${commandToSend} to buoy: ${selectedBuoy}`);
      const res = await secureApi.post('/api/sendCommand', {
        command: commandToSend, 
        buoyId: selectedBuoy 
      });
      
      console.log(`📤 Command send response status: ${res.status}`);
      const result = await res.json();
      console.log(`📤 Command send response:`, result);
      
      appendLog(`⏳ ${result.response}`);
    } catch (err) {
      console.error(`❌ Command send error:`, err);
      appendLog(`❌ Failed to send command: ${err.message}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await handleCommand(input);
    setInput('');
  };

  return (
    <div className="App-main">
      <main className="App-content">
        <div style={{ 
          background: 'white',
          borderRadius: '8px',
          padding: '1.5rem',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          border: '1px solid #e9ecef',
          marginBottom: '1rem'
        }}>
          <div style={{ 
            borderBottom: '2px solid #007bff', 
            paddingBottom: '0.75rem', 
            marginBottom: '1.5rem' 
          }}>
            <h2 style={{ 
              margin: 0, 
              color: '#2c3e50', 
              fontSize: '1.5rem',
              fontWeight: '600'
            }}>
              Sensor Terminal
              <span style={{
                marginLeft: '1rem',
                padding: '0.25rem 0.5rem',
                backgroundColor: isAdvancedAccess ? '#28a745' : '#17a2b8',
                color: 'white',
                borderRadius: '4px',
                fontSize: '0.8rem',
                fontWeight: '500',
                textTransform: 'uppercase'
              }}>
                {isAdvancedAccess ? 'Advanced Access' : 'Standard Access'}
              </span>
            </h2>
          </div>

          {/* Buoy Selection Buttons */}
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {buoyOptions.map((buoy) => (
                <button
                  key={buoy}
                  onClick={() => setSelectedBuoy(buoy)}
                  style={{
                    padding: '0.5rem 1rem',
                    border: '1px solid #dee2e6',
                    background: selectedBuoy === buoy ? '#007bff' : 'white',
                    color: selectedBuoy === buoy ? 'white' : '#495057',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedBuoy !== buoy) {
                      e.target.style.backgroundColor = '#f8f9fa'
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedBuoy !== buoy) {
                      e.target.style.backgroundColor = 'white'
                    }
                  }}
                >
                  {buoy}
                </button>
              ))}
            </div>
          </div>

          {/* Command Input */}
          <form onSubmit={handleSubmit} style={{ marginBottom: '1.5rem' }}>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={isAdvancedAccess 
                ? "Type any command (e.g., PING, SMPINT,600,300, custom commands, or help)" 
                : "Type a command (e.g., PING, SMPINT,600,300, or help)"}
              style={{ 
                width: '100%', 
                padding: '0.75rem',
                border: '1px solid #ced4da',
                borderRadius: '4px',
                fontSize: '0.9rem',
                fontFamily: 'monospace',
                backgroundColor: 'white',
                color: '#495057'
              }}
            />
          </form>

          {/* Terminal Output */}
          <div style={{
            backgroundColor: '#1e1e1e',
            color: '#00ff00',
            padding: '1rem',
            minHeight: '400px',
            maxHeight: '600px',
            overflowY: 'auto',
            borderRadius: '8px',
            border: '1px solid #343a40',
            fontFamily: 'monospace',
            fontSize: '0.9rem',
            lineHeight: '1.4',
            textAlign: 'left'
          }}>
            <div style={{ color: '#ffc107', marginBottom: '0.5rem', textAlign: 'left' }}>
              💡 Enter <strong>PING</strong> to test connectivity
            </div>
            <div style={{ color: '#6c757d', marginBottom: '0.5rem', textAlign: 'left' }}>
              💡 Type <strong>help</strong> for available commands
            </div>
            <div style={{ color: '#6c757d', marginBottom: '0.5rem', textAlign: 'left' }}>
              💡 Use <strong>q</strong> to view queue, <strong>delete q</strong> to clear queue
            </div>
            {isAdvancedAccess && (
              <div style={{ color: '#28a745', marginBottom: '1rem', textAlign: 'left' }}>
                🔓 <strong>Advanced Mode:</strong> Any command allowed - use with caution
              </div>
            )}
            {!isAdvancedAccess && (
              <div style={{ color: '#ffc107', marginBottom: '1rem', textAlign: 'left' }}>
                🔒 <strong>Standard Mode:</strong> Only validated commands allowed
              </div>
            )}
            {log.map((line, index) => (
              <div key={index} style={{ marginBottom: '0.25rem', textAlign: 'left' }}>
                {line}
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};

export default TerminalPage;
